import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Info, Calendar, Users, Scale, MapPin } from 'lucide-react';
import { Button, Card, ConfirmModal, Input, Modal, Select, TextArea } from './ui';
import { useUpdateCase, useArchiveCase } from '../hooks/useCases';
import { useCounties, useCourtsInCounty } from '../hooks/useCourts';
import { setNavGuard } from '../lib/navGuard';
import type { Case } from '../types';

interface CaseSettingsProps {
  caseData: Case;
  onArchived: () => void;
}

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border-subtle">
        <Icon size={16} className="text-green-700" />
        <h3 className="t-card text-green-900">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

export default function CaseSettings({ caseData, onArchived }: CaseSettingsProps) {
  const [name, setName]                       = useState(caseData.name);
  const [clientName, setClientName]           = useState(caseData.client_name || '');
  const [opposingParty, setOpposingParty]     = useState(caseData.opposing_party_name || '');
  const [marriageDate, setMarriageDate]       = useState(caseData.marriage_date || '');
  const [separationDate, setSeparationDate]   = useState(caseData.separation_date || '');
  const [notes, setNotes]                     = useState(caseData.notes || '');
  const [causeNumber, setCauseNumber]         = useState(caseData.cause_number || '');
  const [court, setCourt]                     = useState(caseData.court || '');
  const [county, setCounty]                   = useState(caseData.county || '');
  const [courtKey, setCourtKey]               = useState(caseData.court_key || '');
  // Directory mode is the default; a legacy free-text court without a
  // directory key opens in manual mode so nothing looks lost.
  const [manualCourt, setManualCourt]         = useState(!caseData.court_key && !!caseData.court);
  const [pickerCounty, setPickerCounty]       = useState(caseData.court_details?.county || caseData.county || '');
  const [counselName, setCounselName]         = useState(caseData.opposing_counsel_name || '');
  const [counselFirm, setCounselFirm]         = useState(caseData.opposing_counsel_firm || '');
  const [counselAddress, setCounselAddress]   = useState(caseData.opposing_counsel_address || '');
  const [counselEmail, setCounselEmail]       = useState(caseData.opposing_counsel_email || '');
  const [filingDate, setFilingDate]           = useState(caseData.filing_date || '');
  const [trialDate, setTrialDate]             = useState(caseData.trial_date || '');
  const [mediationDate, setMediationDate]     = useState(caseData.mediation_date || '');
  const [tempOrdersDate, setTempOrdersDate]   = useState(caseData.temp_orders_date || '');
  const [decreedDate, setDecreedDate]         = useState(caseData.divorce_decree_date || '');
  const [clientRole, setClientRole]           = useState<string>(caseData.client_role || '');
  const [hasChildren, setHasChildren]         = useState<boolean | null>(caseData.has_children ?? null);
  const [numChildren, setNumChildren]         = useState<string>(caseData.num_children != null ? String(caseData.num_children) : '');
  const [clientDob, setClientDob]             = useState(caseData.client_dob || '');
  const [clientResidence, setClientResidence] = useState(caseData.client_residence_address || '');
  const [clientSpouseRole, setClientSpouseRole] = useState<string>(caseData.client_spouse_role || '');
  const [showTrashConfirm, setShowTrashConfirm] = useState(false);
  const [saved, setSaved]                     = useState(false);
  const [pendingProceed, setPendingProceed]   = useState<(() => void) | null>(null);

  const navigate = useNavigate();
  const updateCase = useUpdateCase();
  const archiveCase = useArchiveCase();
  const { data: counties = [] } = useCounties();
  const { data: countyCourts = [] } = useCourtsInCounty(manualCourt ? '' : pickerCounty);

  const selectedCourt =
    countyCourts.find(c => c.court_key === courtKey) ||
    (caseData.court_key === courtKey ? caseData.court_details : null);

  const dirty =
    name !== caseData.name ||
    clientName !== (caseData.client_name || '') ||
    opposingParty !== (caseData.opposing_party_name || '') ||
    marriageDate !== (caseData.marriage_date || '') ||
    separationDate !== (caseData.separation_date || '') ||
    notes !== (caseData.notes || '') ||
    causeNumber !== (caseData.cause_number || '') ||
    court !== (caseData.court || '') ||
    county !== (caseData.county || '') ||
    courtKey !== (caseData.court_key || '') ||
    counselName !== (caseData.opposing_counsel_name || '') ||
    counselFirm !== (caseData.opposing_counsel_firm || '') ||
    counselAddress !== (caseData.opposing_counsel_address || '') ||
    counselEmail !== (caseData.opposing_counsel_email || '') ||
    filingDate !== (caseData.filing_date || '') ||
    trialDate !== (caseData.trial_date || '') ||
    mediationDate !== (caseData.mediation_date || '') ||
    tempOrdersDate !== (caseData.temp_orders_date || '') ||
    decreedDate !== (caseData.divorce_decree_date || '') ||
    clientRole !== (caseData.client_role || '') ||
    clientDob !== (caseData.client_dob || '') ||
    clientResidence !== (caseData.client_residence_address || '') ||
    clientSpouseRole !== (caseData.client_spouse_role || '') ||
    hasChildren !== (caseData.has_children ?? null) ||
    numChildren !== (caseData.num_children != null ? String(caseData.num_children) : '');

  useEffect(() => {
    setName(caseData.name);
    setClientName(caseData.client_name || '');
    setOpposingParty(caseData.opposing_party_name || '');
    setMarriageDate(caseData.marriage_date || '');
    setSeparationDate(caseData.separation_date || '');
    setNotes(caseData.notes || '');
    setCauseNumber(caseData.cause_number || '');
    setCourt(caseData.court || '');
    setCounty(caseData.county || '');
    setCourtKey(caseData.court_key || '');
    setManualCourt(!caseData.court_key && !!caseData.court);
    setPickerCounty(caseData.court_details?.county || caseData.county || '');
    setCounselName(caseData.opposing_counsel_name || '');
    setCounselFirm(caseData.opposing_counsel_firm || '');
    setCounselAddress(caseData.opposing_counsel_address || '');
    setCounselEmail(caseData.opposing_counsel_email || '');
    setFilingDate(caseData.filing_date || '');
    setTrialDate(caseData.trial_date || '');
    setMediationDate(caseData.mediation_date || '');
    setTempOrdersDate(caseData.temp_orders_date || '');
    setDecreedDate(caseData.divorce_decree_date || '');
    setClientRole(caseData.client_role || '');
    setClientDob(caseData.client_dob || '');
    setClientResidence(caseData.client_residence_address || '');
    setClientSpouseRole(caseData.client_spouse_role || '');
    setHasChildren(caseData.has_children ?? null);
    setNumChildren(caseData.num_children != null ? String(caseData.num_children) : '');
  }, [caseData]);

  const buildUpdates = () => ({
    name: name || caseData.name,
    client_name: clientName || undefined,
    opposing_party_name: opposingParty || undefined,
    marriage_date: marriageDate || undefined,
    separation_date: separationDate || undefined,
    notes: notes || undefined,
    cause_number: causeNumber || undefined,
    court: court || undefined,
    county: county || undefined,
    // Always sent: '' clears a previously saved directory selection.
    court_key: courtKey,
    opposing_counsel_name: counselName || undefined,
    opposing_counsel_firm: counselFirm || undefined,
    opposing_counsel_address: counselAddress || undefined,
    opposing_counsel_email: counselEmail || undefined,
    filing_date: filingDate || undefined,
    trial_date: trialDate || undefined,
    mediation_date: mediationDate || undefined,
    temp_orders_date: tempOrdersDate || undefined,
    divorce_decree_date: decreedDate || undefined,
    client_role: clientRole || undefined,
    client_dob: clientDob || undefined,
    client_residence_address: clientResidence || undefined,
    client_spouse_role: clientSpouseRole || undefined,
    has_children: hasChildren ?? undefined,
    num_children: hasChildren && numChildren ? parseInt(numChildren) : undefined,
  });

  const handleSave = (onDone?: () => void) => {
    updateCase.mutate(
      { id: caseData.public_id, updates: buildUpdates() },
      {
        onSuccess: () => {
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
          onDone?.();
        },
      }
    );
  };

  // Warn before leaving (browser tab close / refresh) with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Guard programmatic navigation (case selector, New Case button) while dirty
  useEffect(() => {
    if (!dirty) {
      setNavGuard(null);
      return;
    }
    setNavGuard((proceed) => {
      setPendingProceed(() => proceed);
      return false;
    });
    return () => setNavGuard(null);
  }, [dirty]);

  // Intercept in-app link (anchor) navigation while there are unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || anchor.target === '_blank') return;
      let dest: URL;
      try {
        dest = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (dest.origin !== window.location.origin) return;
      if (dest.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      const path = dest.pathname + dest.search + dest.hash;
      setPendingProceed(() => () => navigate(path));
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [dirty, navigate]);

  const runPending = () => {
    const proceed = pendingProceed;
    setPendingProceed(null);
    proceed?.();
  };

  const handleSaveAndLeave = () => {
    handleSave(() => runPending());
  };

  const handleArchive = () => {
    archiveCase.mutate(caseData.public_id, {
      onSuccess: () => {
        setShowTrashConfirm(false);
        onArchived();
      },
    });
  };

  const handlePickCounty = (c: string) => {
    setPickerCounty(c);
    setCourtKey('');
    setCourt('');
    setCounty(c);
  };

  const handlePickCourt = (key: string) => {
    setCourtKey(key);
    if (!key) {
      setCourt('');
      return;
    }
    const ct = countyCourts.find(x => x.court_key === key);
    if (ct) {
      setCourt(ct.name);
      setCounty(ct.county);
    }
  };

  const switchToManual = () => {
    setManualCourt(true);
    setCourtKey('');
  };

  const switchToDirectory = () => {
    setManualCourt(false);
    // If the saved directory selection still matches the (untouched) text
    // fields, restore it so a no-op toggle round-trip stays a no-op.
    if (caseData.court_key && court === (caseData.court || '') && county === (caseData.county || '')) {
      setCourtKey(caseData.court_key);
      setPickerCounty(caseData.court_details?.county || county);
      return;
    }
    setPickerCounty(counties.includes(county) ? county : '');
  };

  // Extra classes layered onto a secondary <Button> to express the selected state.
  const pillBtn = (active: boolean) =>
    active ? 'bg-green-700 text-white border-green-700 hover:bg-green-700' : '';

  return (
    <div className="flex flex-col h-full bg-cream-light">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 max-w-7xl mx-auto pb-24">

          {/* ── Case Info ────────────────────────────────────────────── */}
          <SectionCard icon={Info} title="Case Info">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Case Name" type="text" value={name} onChange={e => setName(e.target.value)} />
              <Input label="Client Name" type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client name" />
              <Input label="Opposing Party" type="text" value={opposingParty} onChange={e => setOpposingParty(e.target.value)} placeholder="Opposing party name" />
              <Input label="Cause Number" type="text" value={causeNumber} onChange={e => setCauseNumber(e.target.value)} placeholder="e.g., 2024-12345-F" />
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                {manualCourt ? (
                  <>
                    <Input label="Court" type="text" value={court} onChange={e => setCourt(e.target.value)} placeholder="e.g., 302nd District Court" />
                    <Input label="County" type="text" value={county} onChange={e => setCounty(e.target.value)} placeholder="e.g., Dallas" />
                  </>
                ) : (
                  <>
                    <Select
                      label="County (Texas)"
                      value={pickerCounty}
                      onChange={e => handlePickCounty(e.target.value)}
                      options={[
                        { value: '', label: '— Select county —' },
                        ...counties.map(c => ({ value: c, label: c })),
                      ]}
                    />
                    <Select
                      label="Court"
                      value={courtKey}
                      onChange={e => handlePickCourt(e.target.value)}
                      disabled={!pickerCounty}
                      options={[
                        { value: '', label: pickerCounty ? '— Select court —' : 'Select a county first' },
                        ...countyCourts.map(c => ({
                          value: c.court_key,
                          label: c.is_family_designated ? `${c.name} · family` : c.name,
                        })),
                      ]}
                    />
                  </>
                )}
                <div className="md:col-span-2 -mt-2 flex items-start justify-between gap-3">
                  {!manualCourt && courtKey && selectedCourt ? (
                    <span className="flex items-center gap-1 text-xs text-green-700">
                      <MapPin size={12} />
                      {[selectedCourt.address, selectedCourt.city, `TX ${selectedCourt.zip_code || ''}`.trim()]
                        .filter(Boolean).join(', ')}
                      {selectedCourt.phone ? ` · ${selectedCourt.phone}` : ''}
                    </span>
                  ) : <span />}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={manualCourt ? switchToDirectory : switchToManual}
                    className="h-auto px-0 text-xs font-normal text-green-700 underline hover:bg-transparent hover:text-green-900"
                  >
                    {manualCourt ? 'Choose from the Texas court directory' : 'Court not listed? Enter manually'}
                  </Button>
                </div>
              </div>
              <Input label="Client Date of Birth" type="date" value={clientDob} onChange={e => setClientDob(e.target.value)} />
              <div className="md:col-span-2">
                <Input label="Client Residence Address" type="text" value={clientResidence} onChange={e => setClientResidence(e.target.value)} placeholder="For the sworn declaration (Tex. CPRC §132.001) — street, city, state, ZIP" />
              </div>
              <div className="md:col-span-2">
                <TextArea label="Notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Case notes..." rows={3} />
              </div>
            </div>
          </SectionCard>

          {/* ── Timeline ─────────────────────────────────────────────── */}
          <SectionCard icon={Calendar} title="Timeline">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Date of Marriage" type="date" value={marriageDate} onChange={e => setMarriageDate(e.target.value)} />
              <Input label="Date of Separation" type="date" value={separationDate} onChange={e => setSeparationDate(e.target.value)} />
              <Input label="Date Filed" type="date" value={filingDate} onChange={e => setFilingDate(e.target.value)} />
              <Input label="Temp Orders Hearing" type="date" value={tempOrdersDate} onChange={e => setTempOrdersDate(e.target.value)} />
              <Input label="Mediation Date" type="date" value={mediationDate} onChange={e => setMediationDate(e.target.value)} />
              <Input label="Trial Date" type="date" value={trialDate} onChange={e => setTrialDate(e.target.value)} />
              <Input label="Divorce Decree Date" type="date" value={decreedDate} onChange={e => setDecreedDate(e.target.value)} />
            </div>
          </SectionCard>

          {/* ── Parties ──────────────────────────────────────────────── */}
          <SectionCard icon={Users} title="Parties">
            <div className="space-y-5">
              <div>
                <label className="block text-label text-ink mb-2">Client is</label>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" size="lg" onClick={() => setClientRole(clientRole === 'Petitioner' ? '' : 'Petitioner')} className={pillBtn(clientRole === 'Petitioner')}>Petitioner</Button>
                  <Button type="button" variant="secondary" size="lg" onClick={() => setClientRole(clientRole === 'Respondent' ? '' : 'Respondent')} className={pillBtn(clientRole === 'Respondent')}>Respondent</Button>
                </div>
              </div>

              <div>
                <label className="block text-label text-ink mb-2">
                  Client's role in the marriage <span className="text-xs font-normal text-ink-faint">(sets which party is Husband / Wife in the Inventory caption)</span>
                </label>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" size="lg" onClick={() => setClientSpouseRole(clientSpouseRole === 'Husband' ? '' : 'Husband')} className={pillBtn(clientSpouseRole === 'Husband')}>Husband</Button>
                  <Button type="button" variant="secondary" size="lg" onClick={() => setClientSpouseRole(clientSpouseRole === 'Wife' ? '' : 'Wife')} className={pillBtn(clientSpouseRole === 'Wife')}>Wife</Button>
                </div>
              </div>

              <div>
                <label className="block text-label text-ink mb-2">Minor Children</label>
                <div className="flex flex-wrap gap-2 items-center">
                  <Button type="button" variant="secondary" size="lg" onClick={() => setHasChildren(hasChildren === true ? null : true)} className={pillBtn(hasChildren === true)}>Yes</Button>
                  <Button type="button" variant="secondary" size="lg" onClick={() => { setHasChildren(hasChildren === false ? null : false); setNumChildren(''); }} className={pillBtn(hasChildren === false)}>No</Button>
                  {hasChildren === true && (
                    <div className="ml-2 flex items-center gap-2">
                      <span className="text-sm text-green-900">How many?</span>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        value={numChildren}
                        onChange={e => setNumChildren(e.target.value)}
                        placeholder="2"
                        className="w-20 text-green-900"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── Opposing Counsel ─────────────────────────────────────── */}
          <SectionCard icon={Scale} title="Opposing Counsel">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Attorney Name" type="text" value={counselName} onChange={e => setCounselName(e.target.value)} placeholder="e.g., Jane Smith" />
              <Input label="Firm Name" type="text" value={counselFirm} onChange={e => setCounselFirm(e.target.value)} placeholder="e.g., Smith & Jones LLP" />
              <div className="md:col-span-2">
                <Input label="Email" type="email" value={counselEmail} onChange={e => setCounselEmail(e.target.value)} placeholder="attorney@firm.com" />
              </div>
              <div className="md:col-span-2">
                <TextArea label="Mailing Address" value={counselAddress} onChange={e => setCounselAddress(e.target.value)} placeholder={`123 Main St\nDallas, TX 75201`} rows={2} />
              </div>
            </div>
          </SectionCard>

        </div>
      </div>

      {/* ── Sticky action bar ──────────────────────────────────────── */}
      <div className="sticky bottom-0 left-0 right-0 bg-cream-light border-t border-border px-6 py-3 flex items-center justify-between">
        <Button
          variant="danger-ghost"
          icon={Trash2}
          onClick={() => setShowTrashConfirm(true)}
          className="-ml-3"
        >
          Move to Trash
        </Button>
        <div className="flex items-center gap-3">
          {saved && <span className="text-good text-sm font-medium">Saved!</span>}
          {dirty && !saved && (
            <span className="text-warn text-sm font-medium">Unsaved changes</span>
          )}
          <Button variant="primary" onClick={() => handleSave()} loading={updateCase.isPending}>
            Save Info
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={showTrashConfirm}
        onClose={() => setShowTrashConfirm(false)}
        onConfirm={handleArchive}
        title="Move to Trash"
        message={`Are you sure you want to move "${caseData.name}" to trash? You can restore it later.`}
        confirmLabel="Move to Trash"
        variant="danger"
        loading={archiveCase.isPending}
      />

      <Modal
        open={pendingProceed !== null}
        onClose={() => setPendingProceed(null)}
        title=""
        footer={
          <div className="flex justify-center gap-2 w-full">
            <Button variant="ghost" onClick={runPending} disabled={updateCase.isPending}>
              Leave without saving
            </Button>
            <Button variant="primary" onClick={handleSaveAndLeave} loading={updateCase.isPending}>
              Save changes
            </Button>
          </div>
        }
      >
        <p className="text-center text-base text-green-900">
          Do you want to save your changes before leaving?
        </p>
      </Modal>
    </div>
  );
}
